import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { masterService } from '../../services/api/master.service';

export const fetchMasters = createAsyncThunk(
  'master/fetchMasters',
  async (params, { rejectWithValue }) => {
    try {
      const response = await masterService.getMasters(params);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch masters');
    }
  }
);
export const fetchStateMasters = createAsyncThunk(
  'master/fetchStateMasters',
  async (_, { rejectWithValue }) => { 
    try {
      const response = await masterService.getStateMasters();
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch masters');
    }
  }
);
export const fetchDynamicParents = createAsyncThunk(
  'master/fetchDynamicParents',
  async (params, { rejectWithValue }) => {
    try {
      const response = await masterService.dynamicParents(params);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch dynamic parents');
    }
  }
);

export const fetchDynamicChildren = createAsyncThunk(
  'master/fetchDynamicChildren',
  async ({ parent_id, sts }, { rejectWithValue }) => {
    try {
      const response = await masterService.getDynamicChildren({ mm_id: parent_id, sts });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch dynamic children');
    }
  }
);

export const uploadMasterFile = createAsyncThunk(
  'master/uploadMasterFile',
  async (fileData, { rejectWithValue }) => {
    try {
      const response = await masterService.bulkUploadMasters(fileData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'File upload failed');
    }
  }
);

export const uploadPincodeMasterFile = createAsyncThunk(
  'master/uploadPincodeMasterFile',
  async (fileData, { rejectWithValue }) => {
    try {
      const response = await masterService.bulkUploadPincodeMasters(fileData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Pincode file upload failed');
    }
  }
);

export const bulkUpdateChildMasters = createAsyncThunk(
  'master/bulkUpdateChildMasters',
  async (fileData, { rejectWithValue }) => {
    try {
      const response = await masterService.bulkUpdateChildMasters(fileData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Bulk update failed');
    }
  }
);

export const bulkUpdateDynamicChildMasters = createAsyncThunk(
  'master/bulkUpdateDynamicChildMasters',
  async ({ formData, mm_id }, { rejectWithValue }) => {
    try {
      const response = await masterService.bulkUpdateDynamicChildMasters(formData, mm_id);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Bulk update failed');
    }
  }
);

export const createBulkMasters = createAsyncThunk(
  'master/createBulkMasters',
  async (mastersData, { rejectWithValue }) => {
    try {
      const response = await masterService.createBulkMasters(mastersData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create masters');
    }
  }
);

export const updateDynamicChild = createAsyncThunk(
  'master/updateDynamicChild',
  async (data, { rejectWithValue }) => {
    try {
      const response = await masterService.updateDynamicChild(data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update dynamic child');
    }
  }
);

export const fetchTseBdrMaster = createAsyncThunk(
  'master/fetchTseBdrMaster',
  async (params, { rejectWithValue }) => {
    try {
      const response = await masterService.getTseBdrMaster(params);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch TSE BDR master');
    }
  }
);

export const fetchTseBdrByCode = createAsyncThunk(
  'master/fetchTseBdrByCode',
  async (tseBdrCode, { rejectWithValue }) => {
    try {
      const response = await masterService.getTseBdrByCode(tseBdrCode);
      const data = response.data.data;
      return Array.isArray(data) ? data[0] : data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch TSE BDR record');
    }
  }
);

export const uploadTseBdrExcel = createAsyncThunk(
  'master/uploadTseBdrExcel',
  async (formData, { rejectWithValue }) => {
    try {
      const response = await masterService.uploadTseBdrExcel(formData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'TSE/BDR file upload failed');
    }
  }
);

export const uploadHdfcBranchLocation = createAsyncThunk(
  'master/uploadHdfcBranchLocation',
  async (formData, { rejectWithValue }) => {
    try {
      const response = await masterService.uploadHdfcBranchLocation(formData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'HDFC Branch Location file upload failed');
    }
  }
);

export const uploadHdfcBranchSubLocation = createAsyncThunk(
  'master/uploadHdfcBranchSubLocation',
  async (formData, { rejectWithValue }) => {
    try {
      const response = await masterService.uploadHdfcBranchSubLocation(formData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'HDFC Branch Sub Location file upload failed');
    }
  }
);

export const fetchPinCodeRecords = createAsyncThunk(
  'master/fetchPinCodeRecords',
  async ({ limit = 50, page = 1 }, { rejectWithValue }) => {
    try {
      const response = await masterService.getPinCodeRecords(limit, page);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch pincode records');
    }
  }
);

export const fetchPinCodeDetails = createAsyncThunk(
  'master/fetchPinCodeDetails',
  async (pinCode, { rejectWithValue }) => {
    try {
      const response = await masterService.getPinCodeDetails(pinCode);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch pincode details');
    }
  }
);

export const fetchChannelName = createAsyncThunk(
  'master/fetchChannelName',
  async (_, { rejectWithValue }) => {
    try {
      const response = await masterService.fetchChannelName();
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch channel names');
    }
  }
);

export const fetchBranchLocation = createAsyncThunk(
  'master/fetchBranchLocation',
  async (_, { rejectWithValue }) => {
    try {
      const response = await masterService.fetchBranchLocation();
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch branch locations');
    }
  }
);

const initialState = {
  masters: [],
  stateMasters: [],
  dynamicParents: [],
  dynamicChildren: [],
  tseBdrData: [],
  tseBdrDetail: null,
  pinCodeRecords: [],
  pinCodePagination: {
    currentPage: 1,
    limit: 50,
    totalRecords: 0,
    totalPages: 0,
  },
  pinCodeDetails: null,
  channelNames: [],
  branchLocations: [],
  currentMaster: null,
  selectedMaster: null, // Add selectedMaster for view/modify page
  isLoading: false,
  error: null,
  totalCount: 0,
};

const masterSlice = createSlice({
  name: 'master',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setCurrentMaster: (state, action) => {
      state.currentMaster = action.payload;
    },
    setSelectedMaster: (state, action) => {
      state.selectedMaster = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchMasters.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchMasters.fulfilled, (state, action) => {
        state.isLoading = false;
        state.masters = action.payload.masters;
        state.totalCount = action.payload.totalCount;
      })
      .addCase(fetchMasters.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(fetchStateMasters.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchStateMasters.fulfilled, (state, action) => {
        state.isLoading = false;
        state.stateMasters = action.payload;
      })
      .addCase(fetchStateMasters.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(fetchDynamicParents.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchDynamicParents.fulfilled, (state, action) => {
        state.isLoading = false;
        state.dynamicParents = action.payload;
      })
      .addCase(fetchDynamicParents.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(uploadMasterFile.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(uploadMasterFile.fulfilled, (state, action) => {
        state.isLoading = false;
        // Optionally, you can handle the response here, e.g., update state
      })
      .addCase(uploadMasterFile.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(uploadPincodeMasterFile.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(uploadPincodeMasterFile.fulfilled, (state, action) => {
        state.isLoading = false;
        // Optionally, you can handle the response here, e.g., update state
      })
      .addCase(uploadPincodeMasterFile.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(bulkUpdateChildMasters.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(bulkUpdateChildMasters.fulfilled, (state, action) => {
        state.isLoading = false;
        // Optionally, you can handle the response here, e.g., update state
      })
      .addCase(bulkUpdateChildMasters.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(bulkUpdateDynamicChildMasters.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(bulkUpdateDynamicChildMasters.fulfilled, (state, action) => {
        state.isLoading = false;
        // Optionally, you can handle the response here, e.g., update state
      })
      .addCase(bulkUpdateDynamicChildMasters.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(createBulkMasters.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createBulkMasters.fulfilled, (state, action) => {
        state.isLoading = false;
        // You could optionally update the state with the response here
      })
      .addCase(createBulkMasters.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(fetchDynamicChildren.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchDynamicChildren.fulfilled, (state, action) => {
        state.isLoading = false;
        state.dynamicChildren = action.payload.data; // Assuming data is in a 'data' property
      })
      .addCase(fetchDynamicChildren.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(fetchTseBdrMaster.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchTseBdrMaster.fulfilled, (state, action) => {
        state.isLoading = false;
        state.tseBdrData = action.payload;
      })
      .addCase(fetchTseBdrMaster.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(fetchTseBdrByCode.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchTseBdrByCode.fulfilled, (state, action) => {
        state.isLoading = false;
        state.tseBdrDetail = action.payload;
      })
      .addCase(fetchTseBdrByCode.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(uploadTseBdrExcel.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(uploadTseBdrExcel.fulfilled, (state, action) => {
        state.isLoading = false;
      })
      .addCase(uploadTseBdrExcel.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(uploadHdfcBranchLocation.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(uploadHdfcBranchLocation.fulfilled, (state, action) => {
        state.isLoading = false;
      })
      .addCase(uploadHdfcBranchLocation.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(uploadHdfcBranchSubLocation.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(uploadHdfcBranchSubLocation.fulfilled, (state, action) => {
        state.isLoading = false;
      })
      .addCase(uploadHdfcBranchSubLocation.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(fetchPinCodeRecords.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchPinCodeRecords.fulfilled, (state, action) => {
        state.isLoading = false;
        state.pinCodeRecords = action.payload.data || [];
        state.pinCodePagination = {
          currentPage: action.payload.currentPage || 1,
          limit: action.payload.limit || 50,
          totalRecords: action.payload.totalRecords || 0,
          totalPages: action.payload.totalPages || Math.ceil((action.payload.totalRecords || 0) / 50),
        };
      })
      .addCase(fetchPinCodeRecords.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(fetchPinCodeDetails.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchPinCodeDetails.fulfilled, (state, action) => {
        state.isLoading = false;
        state.pinCodeDetails = action.payload.data || action.payload;
      })
      .addCase(fetchPinCodeDetails.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(fetchChannelName.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchChannelName.fulfilled, (state, action) => {
        state.isLoading = false;
        state.channelNames = action.payload.data || [];
      })
      .addCase(fetchChannelName.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(fetchBranchLocation.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchBranchLocation.fulfilled, (state, action) => {
        state.isLoading = false;
        state.branchLocations = action.payload.data || action.payload || [];
      })
      .addCase(fetchBranchLocation.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  },
});

export const { clearError, setCurrentMaster, setSelectedMaster } = masterSlice.actions;
export default masterSlice.reducer;
